import React, { useState, useRef, useEffect, useCallback } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import AdminSidebar from '../../components/navigation/AdminSidebar';
import Icon from '../../components/AppIcon';
import Button from '../../components/ui/Button';
import Input from '../../components/ui/Input';
import DishEditorRow from './components/DishEditorRow';
import BulkEditPanel from './components/BulkEditPanel';
import ChangeHistoryPanel from './components/ChangeHistoryPanel';
import SyncValidationToast from './components/SyncValidationToast';
import QuickActionBar from '../restaurant-admin-dashboard/components/QuickActionBar';
import AddDishModal from '../restaurant-admin-dashboard/components/AddDishModal';
import ErrorToast from '../../components/ui/ErrorToast';
import { categoriesForEditor } from '../../data/categoriesData';

import { fetchDishes, upsertDish, upsertDishes, fetchCategories, subscribeToMenuDishes, subscribeToCategories, deleteDish } from '../../services/menuDataService';
import { useAuth } from '../../contexts/AuthContext';
import useEffectiveRestaurantId from '../../hooks/useEffectiveRestaurantId';

// Helper to generate unique toast IDs
let _toastId = 0;
const newToastId = () => `toast-${++_toastId}`;

const ManagementContentEditor = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { isSuperAdmin, profileLoading } = useAuth();
  const restaurantId = useEffectiveRestaurantId();
  const showRestaurantRequired = !profileLoading && !restaurantId;

  // Ref to always have the latest restaurantId in async handlers
  const restaurantIdRef = useRef(restaurantId);
  useEffect(() => { restaurantIdRef.current = restaurantId; }, [restaurantId]);

  // Track in-flight saves so realtime re-fetches don't overwrite optimistic state
  const savingCountRef = useRef(0);

  const requireRestaurantForSave = () => {
    const id = restaurantIdRef?.current;
    if (id) return true;
    addToast(
      isSuperAdmin
        ? 'Cannot save: no restaurant selected. Open a restaurant from Super Admin first (?restaurant_id=...).'
        : 'Cannot save: restaurant not linked to your account. Please contact support.',
      'error',
    );
    return false;
  };
  const importInputRef = useRef(null);
  const topScrollRef = useRef(null);
  const tableScrollRef = useRef(null);
  const topScrollInnerRef = useRef(null);
  const [isSidebarCollapsed, setIsSidebarCollapsed] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [filterCategory, setFilterCategory] = useState('all');
  const [selectedDishes, setSelectedDishes] = useState([]);
  const [changeHistory, setChangeHistory] = useState([]);
  const [isAddDishModalOpen, setIsAddDishModalOpen] = useState(false);
  const [importError, setImportError] = useState('');
  const [syncValidations, setSyncValidations] = useState([]);
  const [isLoading, setIsLoading] = useState(true);

  // Error toasts state
  const [errorToasts, setErrorToasts] = useState([]);
  const addToast = (message, type = 'error') =>
    setErrorToasts(prev => [...prev, { id: newToastId(), message, type }]);
  const dismissToast = (id) =>
    setErrorToasts(prev => prev?.filter(t => t?.id !== id));

  // ── Categories from Supabase ───────────────────────────────────────────────
  const [categories, setCategories] = useState(categoriesForEditor);
  const [rawCategories, setRawCategories] = useState([]);

  useEffect(() => {
    if (!restaurantId) return;
    fetchCategories(restaurantId)?.then((cats) => {
        setRawCategories(cats || []);
        setCategories([
          { value: 'all', label: 'Toutes les catégories' },
          ...cats?.map(cat => ({ value: cat?.id, label: cat?.name })),
        ]);
      })?.catch(() => {});

    const channel = subscribeToCategories(() => {
      fetchCategories(restaurantId)?.then((cats) => {
        setRawCategories(cats || []);
        setCategories([
          { value: 'all', label: 'Toutes les catégories' },
          ...cats?.map(cat => ({ value: cat?.id, label: cat?.name })),
        ]);
      })?.catch(() => {});
    }, restaurantId);
    return () => { try { channel?.unsubscribe?.(); } catch (e) {} };
  }, [restaurantId]);

  // ── Dishes from Supabase ───────────────────────────────────────────────────
  const [dishes, setDishes] = useState([]);
  // Keep a ref so async handlers always read the latest dishes (avoids stale closure)
  const dishesRef = useRef(dishes);
  useEffect(() => { dishesRef.current = dishes; }, [dishes]);

  useEffect(() => {
    if (profileLoading) return;

    if (!restaurantId) {
      setDishes([]);
      setIsLoading(false);
      return;
    }

    setIsLoading(true);
    fetchDishes(restaurantId)
      ?.then((data) => { setDishes(data); dishesRef.current = data; })
      ?.catch(() => addToast('Impossible de charger les plats depuis Supabase.', 'error'))
      ?.finally(() => setIsLoading(false));

    const channel = subscribeToMenuDishes(() => {
      // Skip re-fetch if a save is currently in-flight to avoid overwriting optimistic state
      if (savingCountRef?.current > 0) return;
      fetchDishes(restaurantId)
        ?.then((data) => { setDishes(data); dishesRef.current = data; })
        ?.catch(() => {});
    }, restaurantId);
    return () => { try { channel?.unsubscribe?.(); } catch (e) {} };
  }, [restaurantId, profileLoading]);

  const filteredDishes = dishes?.filter(dish => {
    const matchesSearch = dish?.name?.toLowerCase()?.includes(searchQuery?.toLowerCase()) ||
                         dish?.description?.toLowerCase()?.includes(searchQuery?.toLowerCase());
    const matchesCategory = filterCategory === 'all' || dish?.category === filterCategory;
    return matchesSearch && matchesCategory;
  });

  // Sync top scrollbar width with table scroll width
  useEffect(() => {
    if (!isLoading && tableScrollRef?.current && topScrollInnerRef?.current) {
      topScrollInnerRef.current.style.width = tableScrollRef?.current?.scrollWidth + 'px';
    }
  }, [isLoading, filteredDishes]);

  const handleDishUpdate = async (dishId, field, value) => {
    if (!requireRestaurantForSave()) return;

    // Optimistic update
    setDishes(prevDishes =>
      prevDishes?.map(dish =>
        dish?.id === dishId ? { ...dish, [field]: value } : dish
      )
    );

    // Read from ref to avoid stale closure (dishes state may not have updated yet)
    const dish = dishesRef?.current?.find(d => d?.id === dishId);
    const updatedDish = { ...dish, [field]: value };
    const currentRestaurantId = restaurantIdRef?.current;

    savingCountRef.current += 1;
    try {
      await upsertDish(updatedDish, currentRestaurantId);
    } catch (e) {
      // Revert on failure
      setDishes(prevDishes =>
        prevDishes?.map(d => d?.id === dishId ? dish : d)
      );
      addToast('Échec de la sauvegarde du plat. Veuillez réessayer.', 'error');
      return;
    } finally {
      savingCountRef.current = Math.max(0, savingCountRef?.current - 1);
    }

    // Add to change history
    setChangeHistory(prev => [{
      id: Date.now(),
      dishName: dish?.name,
      field,
      timestamp: new Date()?.toLocaleString('fr-CA'),
      action: 'Modification'
    }, ...prev]?.slice(0, 20));

    const targets = ['Menu client'];
    if (['name', 'price', 'description', 'hidden', 'arUrl']?.includes(field)) {
      targets?.push('Tableau de bord');
    }
    setSyncValidations([{
      dishName: dish?.name || 'Plat',
      field,
      synced: true,
      targets,
    }]);
  };

  const handleBulkUpdate = async (field, value) => {
    if (selectedDishes?.length === 0) return;
    if (!requireRestaurantForSave()) return;

    const previousDishes = [...dishesRef?.current];
    setDishes(prevDishes =>
      prevDishes?.map(dish =>
        selectedDishes?.includes(dish?.id) ? { ...dish, [field]: value } : dish
      )
    );

    const toUpdate = dishesRef?.current?.filter(d => selectedDishes?.includes(d?.id))?.map(d => ({ ...d, [field]: value }));
    const currentRestaurantId = restaurantIdRef?.current;

    savingCountRef.current += 1;
    try {
      await upsertDishes(toUpdate, currentRestaurantId);
    } catch (e) {
      setDishes(previousDishes);
      addToast('Échec de la modification en lot. Veuillez réessayer.', 'error');
      return;
    } finally {
      savingCountRef.current = Math.max(0, savingCountRef?.current - 1);
    }

    setChangeHistory(prev => [{
      id: Date.now(),
      dishName: `${selectedDishes?.length} plats`,
      field,
      timestamp: new Date()?.toLocaleString('fr-CA'),
      action: 'Modification en lot'
    }, ...prev]?.slice(0, 20));

    setSyncValidations([{
      dishName: `${selectedDishes?.length} plats`,
      field,
      synced: true,
      targets: ['Menu client', 'Tableau de bord'],
    }]);

    setSelectedDishes([]);
  };

  const handleSelectDish = (dishId) => {
    setSelectedDishes(prev =>
      prev?.includes(dishId)
        ? prev?.filter(id => id !== dishId)
        : [...prev, dishId]
    );
  };

  const handleSelectAll = () => {
    if (selectedDishes?.length === filteredDishes?.length) {
      setSelectedDishes([]);
    } else {
      setSelectedDishes(filteredDishes?.map(d => d?.id));
    }
  };

  const handleDeleteDish = async (dishId) => {
    if (!requireRestaurantForSave()) return;

    const dish = dishesRef?.current?.find(d => d?.id === dishId);
    if (!window.confirm(`Supprimer "${dish?.name}" ? Cette action est irréversible.`)) return;
    const previousDishes = [...dishesRef?.current];
    setDishes(prev => prev?.filter(d => d?.id !== dishId));
    savingCountRef.current += 1;
    try {
      await deleteDish(dishId, restaurantIdRef?.current);
      setChangeHistory(prev => [{
        id: Date.now(),
        dishName: dish?.name,
        field: 'suppression',
        timestamp: new Date()?.toLocaleString('fr-CA'),
        action: 'Suppression'
      }, ...prev]?.slice(0, 20));
    } catch (e) {
      setDishes(previousDishes);
      addToast('Échec de la suppression du plat. Veuillez réessayer.', 'error');
    } finally {
      savingCountRef.current = Math.max(0, savingCountRef?.current - 1);
    }
  };

  const handleAddDish = () => {
    setIsAddDishModalOpen(true);
  };

  const handleSaveNewDish = async (formData) => {
    if (!requireRestaurantForSave()) return;

    const newDish = {
      id: Date.now(),
      name: formData?.name || '',
      description: formData?.description || '',
      price: parseFloat(formData?.price) || 0,
      category: formData?.category || 'mains',
      image: formData?.image || '',
      imageAlt: formData?.name || '',
      arUrl: formData?.arModelUrl || '',
      hidden: false,
      ingredients: [],
      calories: 0,
      isNew: false,
      isPopular: false,
      isVegetarian: false,
      spiceLevel: 0,
    };
    savingCountRef.current += 1;
    try {
      await upsertDish(newDish, restaurantIdRef?.current);
      setDishes(prev => [...prev, newDish]);
      setChangeHistory(prev => [{
        id: Date.now(),
        dishName: newDish?.name,
        field: 'nouveau plat',
        timestamp: new Date()?.toLocaleString('fr-CA'),
        action: 'Ajout'
      }, ...prev]?.slice(0, 20));
    } catch (e) {
      addToast('Échec de l\'ajout du plat. Veuillez réessayer.', 'error');
    } finally {
      savingCountRef.current = Math.max(0, savingCountRef?.current - 1);
    }
    setIsAddDishModalOpen(false);
  };

  const handleImportMenu = () => {
    setImportError('');
    importInputRef?.current?.click();
  };

  const handleFileChange = (e) => {
    const file = e?.target?.files?.[0];
    if (!file) return;

    const reader = new FileReader();
    reader.onload = async (event) => {
      try {
        const text = event?.target?.result;
        let importedDishes = [];

        if (file?.name?.endsWith('.json')) {
          const parsed = JSON.parse(text);
          importedDishes = Array.isArray(parsed) ? parsed : parsed?.dishes || [];
        } else if (file?.name?.endsWith('.csv')) {
          const lines = text?.split('\n')?.filter(l => l?.trim());
          const headers = lines?.[0]?.split(',')?.map(h => h?.trim()?.replace(/"/g, ''));
          importedDishes = lines?.slice(1)?.map((line, idx) => {
            const values = line?.split(',')?.map(v => v?.trim()?.replace(/"/g, ''));
            const obj = {};
            headers?.forEach((h, i) => { obj[h] = values?.[i] || ''; });
            return {
              id: Date.now() + idx,
              name: obj?.name || obj?.Name || '',
              description: obj?.description || obj?.Description || '',
              price: parseFloat(obj?.price || obj?.Price) || 0,
              category: obj?.category || obj?.Category || 'mains',
              image: obj?.image || obj?.Image || '',
              imageAlt: '',
              arUrl: '',
              hidden: false,
            };
          })?.filter(d => d?.name);
        } else {
          setImportError('Format non supporté. Utilisez JSON ou CSV.');
          addToast('Format de fichier non supporté. Utilisez JSON ou CSV.', 'error');
          return;
        }

        if (importedDishes?.length === 0) {
          setImportError('Aucun plat trouvé dans le fichier.');
          addToast('Aucun plat trouvé dans le fichier importé.', 'warning');
          return;
        }

        if (!requireRestaurantForSave()) return;

        await upsertDishes(importedDishes, restaurantId);
        setDishes(prev => [...prev, ...importedDishes]);
        setChangeHistory(prev => [{
          id: Date.now(),
          dishName: `${importedDishes?.length} plats importés`,
          field: 'import',
          timestamp: new Date()?.toLocaleString('fr-CA'),
          action: 'Import'
        }, ...prev]?.slice(0, 20));
      } catch (err) {
        setImportError('Erreur lors de la lecture du fichier. Vérifiez le format.');
        addToast('Erreur lors de la lecture du fichier. Vérifiez le format.', 'error');
      }
    };
    reader.onerror = () => {
      addToast('Impossible de lire le fichier. Vérifiez votre connexion ou les permissions.', 'error');
    };
    reader?.readAsText(file);
    e.target.value = '';
  };

  const handleExportData = () => {
    const exportData = dishes?.map(({ id, name, description, price, category, image, arUrl, hidden }) => ({
      id, name, description, price, category, image, arUrl, hidden
    }));
    const json = JSON.stringify(exportData, null, 2);
    const blob = new Blob([json], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `menu-export-${new Date()?.toISOString()?.slice(0, 10)}.json`;
    document.body?.appendChild(link);
    link?.click();
    document.body?.removeChild(link);
    URL.revokeObjectURL(url);
  };

  const handleGenerateQR = () => {
    navigate(restaurantId ? `/qr-code-generator?restaurant_id=${restaurantId}` : '/qr-code-generator');
  };

  // Management Editor Interface
  return (
    <div className="min-h-screen bg-background">
      {/* Hidden file input for import */}
      <input
        ref={importInputRef}
        type="file"
        accept=".json,.csv"
        className="hidden"
        onChange={handleFileChange}
      />
      <AdminSidebar
        isCollapsed={isSidebarCollapsed}
        onToggle={() => setIsSidebarCollapsed(!isSidebarCollapsed)}
        activeTab="content-editor"
        onTabChange={() => {}}
      />
      <div className={`transition-smooth ${isSidebarCollapsed ? 'lg:ml-20' : 'lg:ml-64'}`}>
        {/* Header */}
        <header className="sticky top-0 z-nav bg-card border-b border-border shadow-warm">
          <div className="container mx-auto px-4 md:px-6 lg:px-8">
            <div className="flex items-center justify-between h-16">
              <div className="flex items-center gap-3">
                <div className="bg-primary/10 rounded-lg p-2">
                  <Icon name="Edit3" size={24} color="var(--color-primary)" />
                </div>
                <div>
                  <h1 className="text-lg font-heading font-bold text-foreground">
                    Éditeur de Contenu
                  </h1>
                  <p className="text-xs text-muted-foreground">Gestion du menu</p>
                </div>
              </div>

              <div className="flex items-center gap-3">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => navigate('/restaurant-menu-landing')}
                  iconName="Eye"
                  iconPosition="left"
                >
                  Aperçu client
                </Button>
              </div>
            </div>
          </div>
        </header>
        <main className="container mx-auto px-4 md:px-6 lg:px-8 py-6">
          {showRestaurantRequired && (
            <div className="mb-4 flex items-start gap-2 bg-amber-50 border border-amber-300 text-amber-900 rounded-lg px-4 py-3 text-sm">
              <Icon name="AlertCircle" size={16} className="mt-0.5 flex-shrink-0" />
              <div>
                <p className="font-medium">No restaurant selected</p>
                <p className="mt-1">
                  {isSuperAdmin
                    ? 'Open a restaurant from the Super Admin dashboard first. The URL must include ?restaurant_id=...' :'Your account is not linked to a restaurant. Saves cannot be sent to Supabase until a restaurant is assigned.'}
                </p>
                {isSuperAdmin && (
                  <Button
                    variant="outline"
                    size="sm"
                    className="mt-3"
                    onClick={() => navigate('/super-admin')}
                  >
                    Go to Super Admin
                  </Button>
                )}
              </div>
            </div>
          )}

          {/* Quick Actions Bar */}
          <QuickActionBar
            onAddDish={handleAddDish}
            onImportMenu={handleImportMenu}
            onExportData={handleExportData}
            onGenerateQR={handleGenerateQR}
          />

          {/* Import error message */}
          {importError && (
            <div className="mb-4 flex items-center gap-2 bg-destructive/10 border border-destructive/30 text-destructive rounded-lg px-4 py-3 text-sm">
              <Icon name="AlertCircle" size={16} />
              <span>{importError}</span>
              <button onClick={() => setImportError('')} className="ml-auto">
                <Icon name="X" size={14} />
              </button>
            </div>
          )}

          {/* Controls */}
          <div className="mb-6 space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div className="md:col-span-2">
                <Input
                  type="text"
                  placeholder="Rechercher un plat..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e?.target?.value)}
                />
              </div>
              <div>
                <select
                  value={filterCategory}
                  onChange={(e) => setFilterCategory(e?.target?.value)}
                  className="w-full h-10 rounded-md border border-input bg-background px-3 py-2 text-sm"
                >
                  {categories?.map(cat => (
                    <option key={cat?.value} value={cat?.value}>{cat?.label}</option>
                  ))}
                </select>
              </div>
            </div>

            {selectedDishes?.length > 0 && (
              <BulkEditPanel
                selectedCount={selectedDishes?.length}
                onBulkUpdate={handleBulkUpdate}
                onClearSelection={() => setSelectedDishes([])}
              />
            )}
          </div>

          {/* Dish Table */}
          <div className="bg-card border border-border rounded-lg shadow-warm overflow-hidden">
            {isLoading ? (
              <div className="text-center py-12">
                <Icon name="Loader2" size={32} className="mx-auto text-muted-foreground mb-3 animate-spin" />
                <p className="text-muted-foreground">Chargement des plats...</p>
              </div>
            ) : (
              <>
                {/* Top scrollbar */}
                <div
                  ref={topScrollRef}
                  className="overflow-x-auto"
                  style={{ height: '12px' }}
                  onScroll={() => {
                    if (tableScrollRef?.current) {
                      tableScrollRef.current.scrollLeft = topScrollRef?.current?.scrollLeft;
                    }
                  }}
                >
                  <div ref={topScrollInnerRef} style={{ height: '1px' }} />
                </div>
                <div
                  ref={tableScrollRef}
                  className="overflow-x-auto"
                  onScroll={() => {
                    if (topScrollRef?.current) {
                      topScrollRef.current.scrollLeft = tableScrollRef?.current?.scrollLeft;
                    }
                  }}
                >
                  <table className="w-full">
                    <thead className="bg-muted/50 border-b border-border">
                      <tr>
                        <th className="px-4 py-3 text-left text-sm font-semibold text-foreground">Photo</th>
                        <th className="px-4 py-3 text-left text-sm font-semibold text-foreground">Nom</th>
                        <th className="px-4 py-3 text-left text-sm font-semibold text-foreground">Description</th>
                        <th className="px-4 py-3 text-left text-sm font-semibold text-foreground">Prix</th>
                        <th className="px-4 py-3 text-left text-sm font-semibold text-foreground">Catégorie</th>
                        <th className="px-4 py-3 text-left text-sm font-semibold text-foreground">URL AR</th>
                        <th className="px-4 py-3 text-left text-sm font-semibold text-foreground">Dimensions AR</th>
                        <th className="px-4 py-3 text-left text-sm font-semibold text-foreground">Badge</th>
                        <th className="px-4 py-3 text-left text-sm font-semibold text-foreground">Visibilité</th>
                        <th className="px-4 py-3 text-left text-sm font-semibold text-foreground">Action</th>
                      </tr>
                    </thead>
                    <tbody>
                      {filteredDishes?.map(dish => (
                        <DishEditorRow
                          key={dish?.id}
                          dish={dish}
                          isSelected={selectedDishes?.includes(dish?.id)}
                          onSelect={handleSelectDish}
                          onUpdate={handleDishUpdate}
                          onDelete={handleDeleteDish}
                          categories={categories}
                          restaurantId={restaurantId}
                        />
                      ))}
                    </tbody>
                  </table>
                </div>
              </>
            )}

            {!isLoading && filteredDishes?.length === 0 && (
              <div className="text-center py-12">
                <Icon name="Search" size={48} className="mx-auto text-muted-foreground mb-3" />
                <p className="text-muted-foreground">Aucun plat trouvé</p>
              </div>
            )}
          </div>

          {/* Change History */}
          <ChangeHistoryPanel history={changeHistory} />
        </main>
      </div>
      {/* Add Dish Modal */}
      <AddDishModal
        isOpen={isAddDishModalOpen}
        onClose={() => setIsAddDishModalOpen(false)}
        onSave={handleSaveNewDish}
        categories={rawCategories}
        restaurantId={restaurantId}
      />
      {/* Sync Validation Toast */}
      <SyncValidationToast
        validations={syncValidations}
        onDismiss={() => setSyncValidations([])}
      />
      {/* Error Toasts */}
      <ErrorToast toasts={errorToasts} onDismiss={dismissToast} />
    </div>
  );
};

export default ManagementContentEditor;